import {
  AdvancedSearchType,
  AdvancedSearchTypeForMarketingAdministrator,
} from "../config/enums";
import { administratorRoles, userHasCorrectRole } from "../config/roles";
import {
  SearchEntityConfig,
  SearchFieldConfig,
  SearchJoin,
} from "./searchEntities.types";

const text = (name: string, label: string, bulkEdit?: boolean): SearchFieldConfig => ({
  name,
  label,
  ...(bulkEdit ? { bulkEdit } : {}),
});

const dateTime = (name: string, label: string): SearchFieldConfig => ({
  name,
  label,
  dateTime: true,
});

const donot = (name: string, label: string, bulkEdit: boolean): SearchFieldConfig => ({
  name,
  label,
  inverted: true,
  bulkEdit,
});

const AddressFields = [
  ["line1", "Address 1 Line 1"],
  ["line2", "Address 1 Line 2"],
  ["line3", "Address 1 Line 3"],
  ["city", "Address 1 City"],
  ["county", "Address 1 County"],
  ["country", "Address 1 Country"],
  ["postalcode", "Address 1 Postal Code"],
] as const;

const addressFields = (prefix: string) =>
  AddressFields.map(([part, label]) => text(`${prefix}address1_${part}`, label));

const modifiedBy: SearchFieldConfig = { name: "modifiedby", label: "Modified By" };

const contactFields: SearchFieldConfig[] = [
  text("firstname", "First Name", true),
  text("lastname", "Last Name", true),
  text("emailaddress1", "Email Address"),
  dateTime("createdon", "Created On"),
  dateTime("modifiedon", "Modified On"),
  modifiedBy,
  { name: "statecode", label: "Status" },
  text("salutation", "Title", true),
  text("middlename", "Middle Name", true),
  text("gendercode", "Gender", true),
  text("telephone1", "Business Phone", true),
  text("telephone2", "Home Phone", true),
  text("mobilephone", "Mobile Phone", true),
  text("g4_age", "Age"),
  text("birthdate", "Birthdate"),
  { name: "parentcustomerid", label: "Account", bulkEdit: true, link: "/account" },
  text("g4api_apisynchronisationrequired", "API Synchronisation Required", true),
  text("jobtitle", "Job Title", true),
  { name: "g4_gdprstatus", label: "GDPR Status", unknownLabel: "Unknown" },
  donot("donotemail", "Send Emails", true),
  donot("donotbulkemail", "Send Bulk Emails", true),
  donot("donotphone", "Allow Phone Calls", true),
  donot("donotpostalmail", "Send Mail", true),
  donot("donotbulkpostalmail", "Send Bulk Mail", true),
  donot("g4c_donotallowsms", "Send SMS", true),
  donot("g4_donotbulkemail3rdparty", "Send Partner Bulk Emails", true),
  donot("g4_donotphone3rdparty", "Allow Partner Phone Calls", true),
  donot("g4_donotpostalmail3rdparty", "Send Partner Bulk Mail", true),
  donot("g4_donotsms3rdparty", "Send Partner SMS", true),
  ...addressFields(""),
];

const audit = (name: string) => `g4gdpr_${name}`;

const contactAuditFields: SearchFieldConfig[] = [
  text(audit("firstname"), "First Name"),
  text(audit("lastname"), "Last Name"),
  text(audit("emailaddress1"), "Email Address"),
  donot(audit("donotbulkemail"), "Send Bulk Emails", false),
  donot(audit("g4_donotbulkemail3rdparty"), "Send Partner Bulk Emails", false),
  donot(audit("g4_donotsms3rdparty"), "Send Partner SMS", false),
  dateTime("createdon", "Created On"),
  dateTime("modifiedon", "Modified On"),
  { name: "statecode", label: "Status" },
  text(audit("salutation"), "Title"),
  text(audit("middlename"), "Middle Name"),
  text(audit("gendercode"), "Gender"),
  text(audit("telephone1"), "Business Phone"),
  text(audit("telephone2"), "Home Phone"),
  text(audit("mobilephone"), "Mobile Phone"),
  text(audit("birthdate"), "Birthdate"),
  text(audit("jobtitle"), "Job Title"),
  { name: audit("g4_gdprstatus"), label: "GDPR Status", unknownLabel: "Unknown" },
  donot(audit("donotemail"), "Send Emails", false),
  donot(audit("donotphone"), "Allow Phone Calls", false),
  donot(audit("donotpostalmail"), "Send Mail", false),
  donot(audit("donotbulkpostalmail"), "Send Bulk Mail", false),
  donot(audit("g4c_donotallowsms"), "Send SMS", false),
  donot(audit("g4_donotphone3rdparty"), "Allow Partner Phone Calls", false),
  donot(audit("g4_donotpostalmail3rdparty"), "Send Partner Bulk Mail", false),
  ...addressFields("g4gdpr_"),
];

const communicationField: SearchFieldConfig = {
  name: "g4c_communicationid",
  label: "Communication",
  link: "/communication",
};

const communicationAuditFields: SearchFieldConfig[] = [
  text("g4c_name", "Name"),
  communicationField,
  { name: "g4c_contactid", label: "Contact", link: "/contact", filter: false },
  text("g4c_fromtest", "From Test"),
  text("g4c_page", "Page"),
  dateTime("g4c_auditdate", "Audit Date"),
  { name: "statecode", label: "Status" },
  text("g4c_additionalinformation", "Additional Information"),
];

const contactJoin = (from: string): SearchJoin => ({
  Alias: "c",
  AllowColumns: true,
  EntityName: "contact",
  From: from,
  Label: "Contact",
  To: "contactid",
});

const SearchEntities: SearchEntityConfig[] = [
  {
    entityName: AdvancedSearchType.Contacts,
    recordRoute: "/contact",
    fields: contactFields,
    defaultColumns: [
      "firstname",
      "lastname",
      "emailaddress1",
      "createdon",
      "modifiedon",
      "modifiedby",
    ],
    defaultRules: ["statecode"],
    defaultOrderBy: "createdon",
    joins: [
      {
        Alias: "ca",
        AllowColumns: false,
        EntityName: "g4c_communicationaudit",
        From: "contactid",
        Label: "Communication Audit",
        To: "g4c_contactid",
      },
      {
        Alias: "lm",
        AllowColumns: false,
        EntityName: "listmember",
        From: "contactid",
        Label: "List Member",
        To: "entityid",
      },
      {
        Alias: "l",
        AllowColumns: false,
        EntityName: "list",
        From: "lm.listid",
        Label: "List",
        To: "listid",
      },
    ],
  },
  {
    entityName: AdvancedSearchType["Contact Audits"],
    recordRoute: "/contact-audit",
    fields: contactAuditFields,
    defaultColumns: [
      audit("firstname"),
      audit("lastname"),
      audit("emailaddress1"),
      audit("donotbulkemail"),
      audit("g4c_donotallowsms"),
      audit("g4_donotbulkemail3rdparty"),
      audit("g4_donotsms3rdparty"),
      "createdon",
      "modifiedon",
    ],
    defaultRules: [],
    defaultOrderBy: "createdon",
    joins: [contactJoin("g4gdpr_contact")],
  },
  {
    entityName: AdvancedSearchType["Communication Audits"],
    recordRoute: "/communication-audit",
    fields: communicationAuditFields,
    defaultColumns: [
      "g4c_communicationid",
      "g4c_contactid",
      "g4c_page",
      "g4c_auditdate",
    ],
    defaultRules: ["g4c_communicationid"],
    defaultOrderBy: "g4c_auditdate",
    joins: [contactJoin("g4c_contactid")],
  },
  {
    entityName: "listmember",
    recordRoute: "",
    fields: [
      {
        name: "listid",
        label: "Static Marketing List",
        link: "/static-marketing-list",
      },
      dateTime("createdon", "Created On"),
    ],
    defaultColumns: [],
    defaultRules: [],
    defaultOrderBy: "createdon",
    joins: [],
  },
];

export function getSearchEntityConfig(entityName: string | undefined) {
  return SearchEntities.find((config) => config.entityName === entityName);
}

/** Entities whose metadata is read for a search; the rest are rejected by the service. */
export const MetadataSearchEntityNames = [
  ...Object.values(AdvancedSearchType),
  "listmember",
] as string[];

// Users who aren't administrators can only search some entities.
export function getSearchEntityTypesForUser(user: unknown) {
  const isNonAdministratorUser = !userHasCorrectRole(user, administratorRoles);

  return {
    allowedEntityTypes: isNonAdministratorUser
      ? AdvancedSearchTypeForMarketingAdministrator
      : null,
    searchTypeEnum: isNonAdministratorUser
      ? AdvancedSearchTypeForMarketingAdministrator
      : AdvancedSearchType,
  };
}
